import { __assign, __rest } from "tslib";
import React, { useMemo, useContext, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import classnames from 'classnames';
import ddt from '@wix/da-ddt';
import { LoadableComponentContext } from '@wix/da-hooks/pkg/useLoadableComponent';
import { BiDataHook } from '@wix/da-bi/pkg/constants';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { useCommentPostAction } from '../../../CommentEditor/hooks/useCommentPostAction';
import { useCommentCancelAction } from '../../../CommentEditor/hooks/useCommentCancelAction';
import LoadingIndicator from '../../../LoadingIndicator';
import LoadingPlaceholder from '../LoadingPlaceholder';
import LazyPlaceholder from '../LazyPlaceholder';
import CommentBoxFooter from '../CommentBoxFooter';
import { CommentEditorContext, EditorContext, } from '../../../CommentEditor/context';
import CommentEditor from '../../../CommentEditor';
import s from './CommentBoxEditor.scss';
/** The core of our comment box. This is a `<CommentEditor />` with a footer and a loading indicator */
var CommentBoxEditor = function (_a) {
    var autoFocus = _a.autoFocus, className = _a.className, footerClassName = _a.footerClassName, onCancel = _a.onCancel, nc = _a.nc, restProps = __rest(_a, ["autoFocus", "className", "footerClassName", "onCancel", "nc"]);
    var _b = useContext(CommentEditorContext), editorId = _b.editorId, isBusy = _b.isBusy, commentableItemId = _b.commentableItemId, commentableType = _b.commentableType, shouldUseFallback = _b.shouldUseFallback, isExpanded = _b.isExpanded, isFocusRequested = _b.isFocusRequested;
    var isLoadingTriggered = useContext(LoadableComponentContext).isLoadingTriggered;
    var _c = useContext(EditorContext), api = _c.api, isEditorReady = _c.isReady;
    var _d = useInView({
        triggerOnce: true,
        threshold: 1,
    }), ref = _d[0], isInView = _d[1];
    var shouldFocus = autoFocus ||
        isFocusRequested ||
        (isFocusRequested && isEditorReady && isInView);
    useEffect(function () {
        if (!api) {
            return;
        }
        if (shouldFocus) {
            ddt.log('comments', 'Focusing CommentBox editor in InView.onChange, id: ', editorId);
            api.focusEditor();
        }
    }, [shouldFocus, api, editorId]);
    // Memo because we want to stop the toolbar from rendering too much
    // this also fixes double click to use the toolbar as it updates as you click
    // and loses the event
    var postComment = useCommentPostAction({
        messageId: nc === null || nc === void 0 ? void 0 : nc.messageId,
        folderId: nc === null || nc === void 0 ? void 0 : nc.folderId,
        shouldDeleteMessage: nc === null || nc === void 0 ? void 0 : nc.shouldDeleteMessage,
    });
    var cancelComment = useCommentCancelAction({ onCancel: onCancel });
    var Footer = useMemo(function () { return (React.createElement(CommentBoxFooter, { onCancel: cancelComment, onPost: postComment, className: classnames(s['commentbox-footer'], footerClassName) })); }, [footerClassName, cancelComment, postComment]);
    return (React.createElement("div", { ref: ref, className: classnames(s['root'], (isExpanded || shouldUseFallback) && s['expanded'], className), "data-hook": BiDataHook.COMMENT_EDITOR },
        isBusy && React.createElement(LoadingIndicator, { className: s['loading'] }),
        !isLoadingTriggered && React.createElement(LazyPlaceholder, null),
        React.createElement(CommentEditor, __assign({ className: classnames(s['commentbox-editor'], (isExpanded || shouldUseFallback) && s['expanded']), loadingIndicator: React.createElement(LoadingPlaceholder, null), onKeyboardSubmit: postComment }, restProps)),
        (shouldUseFallback || isLoadingTriggered) && (React.createElement(BiLoggerContextProvider, { value: {
                itemid: typeof commentableItemId === 'string'
                    ? parseInt(commentableItemId)
                    : commentableItemId,
                typeid: commentableType,
            } }, Footer)),
        nc && nc.postfix));
};
CommentBoxEditor.displayName = 'CommentBoxEditor';
export default CommentBoxEditor;
//# sourceMappingURL=CommentBoxEditor.js.map